import React from 'react';
import { setSelectedTool, setSelectedModel } from '../store/chatModeSlice';
import { clearUploadedFiles, removeUploadedFile, clearTranslationData, clearTargetLanguages } from '../store/chatSlice';
import { hideGlobalBlocking } from '../store/loadingSlice';
import { MODEL_ELIGIBLE_TOOLS, getToolForModelSelection } from '../utils/config/modelConfig';
import type { AppDispatch } from '../store/store';
import type { ChatModeState } from '../store/chatModeSlice';
import type { UploadedFileMeta } from '../store/chatSlice';
import type { ConfirmationConfig, FileWarning } from '../components/Chat/input/types';
import logger from '../utils/logger/logger';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UseChatToolSelectionOptions {
  dispatch: AppDispatch;
  selectedTool: string | null;
  uploadedFiles: UploadedFileMeta[];
  hasMessages?: boolean;
  chatModeState: ChatModeState;
  onClearChat?: () => void;
  shouldClearForTargetTool: (targetTool: string | null | undefined) => boolean;
  isMobile: boolean;
  isTablet: boolean;
  chatPage?: boolean;
  onNavigateToChat?: (message: string, tools?: string[]) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  uploadFilesBatch: (files: { name: string; type: string; data: string }[]) => Promise<void>;
  uploadedFilesRef: React.MutableRefObject<UploadedFileMeta[]>;
  setFileWarning: React.Dispatch<React.SetStateAction<FileWarning | null>>;
  setIsFileUploadDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFileUploadActive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTranslationCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMobileToolsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSpeechListening?: boolean;
}

export interface UseChatToolSelectionReturn {
  showConfirmation: boolean;
  setShowConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  confirmationConfig: ConfirmationConfig | null;
  setConfirmationConfig: React.Dispatch<React.SetStateAction<ConfirmationConfig | null>>;
  handleOptionClick: (apiLabel: string, event?: React.MouseEvent | React.MouseEvent<HTMLButtonElement> & { nativeEvent?: any }) => void;
  handleRemoveTool: () => void;
  proceedWithToolSelection: (apiLabel: string) => void;
  handleModelSelection: (modelId: string) => void;
  triggerAdditionalUpload: () => void;
  manuallyClearedToolRef: React.MutableRefObject<boolean>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useChatToolSelection(options: UseChatToolSelectionOptions): UseChatToolSelectionReturn {
  const {
    dispatch,
    selectedTool,
    uploadedFiles,
    hasMessages,
    onClearChat,
    shouldClearForTargetTool,
    isMobile,
    isTablet,
    chatPage,
    onNavigateToChat,
    textareaRef,
    uploadFilesBatch,
    uploadedFilesRef,
    setFileWarning,
    setIsFileUploadDialogOpen,
    setIsFileUploadActive,
    setIsTranslationCollapsed,
    setIsMobileToolsMenuOpen,
    isSpeechListening,
  } = options;

  // ─── State ───────────────────────────────────────────────────────────────────

  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [confirmationConfig, setConfirmationConfig] = React.useState<ConfirmationConfig | null>(null);

  // Ref to prevent auto-reselection after manual tool clear
  const manuallyClearedToolRef = React.useRef(false);

  // ─── Awaiting file selection state (needed for handleRemoveTool) ──────────────
  const [, setAwaitingFileSelection] = React.useState(false);

  // ─── handleModelSelection ────────────────────────────────────────────────────

  const handleModelSelection = React.useCallback((modelId: string) => {
    dispatch(setSelectedTool(getToolForModelSelection(modelId)));
    dispatch(setSelectedModel(modelId));
  }, [dispatch]);

  // ─── triggerAdditionalUpload ─────────────────────────────────────────────────

  const triggerAdditionalUpload = React.useCallback(() => {
    dispatch(hideGlobalBlocking());
    dispatch(setSelectedTool(null));
    manuallyClearedToolRef.current = true;
    setIsFileUploadDialogOpen(true);
  }, [dispatch, setIsFileUploadDialogOpen]);

  // ─── proceedWithToolSelection ────────────────────────────────────────────────

  const proceedWithToolSelection = React.useCallback((apiLabel: string) => {
    // Close mobile menu if open
    if (isMobile) {
      setIsMobileToolsMenuOpen(false);
    }
    dispatch(setSelectedTool(apiLabel));

    // Hide the File Upload pill whenever a named secondary tool becomes active
    if (apiLabel === 'data_analysis' || apiLabel === 'translation') {
      setIsFileUploadActive(false);
    }

    if (apiLabel === 'data_analysis') {
      // When activating data analysis, evaluate existing files.
      const isValidAnalysisType = (f: any) => {
        const t = (f.type || '').toLowerCase();
        const n = (f.name || '').toLowerCase();
        return t === 'text/csv' || t === 'application/vnd.ms-excel' || t === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || /\.csv$/i.test(n) || /\.(xls|xlsx)$/i.test(n);
      };
      const existing = [...uploadedFilesRef.current];
      const valid = existing.filter(isValidAnalysisType);
      const invalid = existing.filter(f => !isValidAnalysisType(f));
      // Remove invalids with warning alert if any
      if (invalid.length > 0) {
        invalid.forEach(f => dispatch(removeUploadedFile(f.name)));
        // Provide filenames for transparency (truncate if many)
        const names = invalid.map(f => f.name).slice(0, 5);
        const more = invalid.length > names.length ? ` +${invalid.length - names.length} more` : '';
        setFileWarning({ message: `Removed ${invalid.length} invalid file(s): ${names.join(', ')}${more}. Only CSV or Excel (max 2, <16MB each) are allowed for Data Analysis.`, mode: 'general' });
      }
      // If we have valid files already, upload any that lack a fileId
      if (valid.length > 0) {
        const toUpload = valid.filter(f => !f.fileId).map(f => ({ name: f.name, type: f.type, data: f.data }));
        if (toUpload.length > 0) {
          uploadFilesBatch(toUpload).finally(() => {
            dispatch(hideGlobalBlocking());
          });
        }
        // Do NOT trigger file picker since at least one valid file exists.
      } else if (invalid.length === 0) {
        // No valid files and no invalid removal warning -> open FileUploadDialog immediately
        setIsFileUploadDialogOpen(true);
      } else {
        // Invalid removal warning is showing; defer opening dialog until user clicks OK
        // We rely on fileWarning OK handler to detect zero valid files and open dialog then.
      }
    }

    if (apiLabel === 'translation') {
      const willNavigate = !chatPage && onNavigateToChat;
      if (willNavigate) {
        onNavigateToChat('', ['translation']);
      }
    }

    // Focus textarea after tool selection - but not on mobile/tablet to avoid triggering keyboard
    if (!isMobile && !isTablet) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isMobile, isTablet, dispatch, uploadFilesBatch, chatPage, onNavigateToChat,
    setIsMobileToolsMenuOpen, setFileWarning, setIsFileUploadDialogOpen, setIsFileUploadActive,
    textareaRef, uploadedFilesRef]);

  // ─── handleOptionClick ───────────────────────────────────────────────────────

  const handleOptionClick = (apiLabel: string, event?: React.MouseEvent | React.MouseEvent<HTMLButtonElement> & { nativeEvent?: any }) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Check if we have uploaded files and are switching to a different tool (or selecting a tool when none selected)
    if (uploadedFiles.length > 0 && selectedTool !== apiLabel) {
      const hasImages = uploadedFiles.some(f =>
        /image\/(png|jpeg|jpg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name)
      );
      const hasDataFiles = uploadedFiles.some(f =>
        /text\/csv/.test(f.type) || /application\/vnd\.(ms-excel|openxmlformats-officedocument\.spreadsheetml\.sheet)/.test(f.type) ||
        /\.(csv|xls|xlsx)$/i.test(f.name)
      );
      const hasTextFiles = uploadedFiles.some(f =>
        /application\/(pdf|msword|vnd\.openxmlformats-officedocument\.wordprocessingml\.document|vnd\.(ms-powerpoint|openxmlformats-officedocument\.presentationml\.presentation))/.test(f.type) ||
        /\.(pdf|doc|docx|ppt|pptx|txt)$/i.test(f.name)
      );

      let shouldWarn = false;
      let warningMessage = '';

      // Check for incompatible tool selections based on file types
      if (apiLabel === 'reasoning' && (hasImages || hasDataFiles || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'Reasoning mode does not support uploaded files. Switching to this mode will clear your chat history and remove all uploaded files. Are you sure you want to continue?';
      } else if (apiLabel === 'image_generation' && (hasDataFiles || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'The Image Generation tool only supports image files. Switching to this tool will clear your chat history and remove all incompatible files. Are you sure you want to continue?';
      } else if (apiLabel === 'web_search' && hasDataFiles) {
        shouldWarn = true;
        warningMessage = 'The Web Search tool does not support data analysis files (CSV/Excel). Switching to this tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      } else if (apiLabel === 'data_analysis' && (hasImages || hasTextFiles)) {
        shouldWarn = true;
        warningMessage = 'The Data Analysis tool only supports CSV and Excel files. Switching to this tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      } else if (selectedTool === 'translation' && apiLabel !== 'translation' && (hasTextFiles || hasDataFiles)) {
        shouldWarn = true;
        warningMessage = 'The Document Translation tool only supports document files (PDF, Word, PowerPoint, Excel). Switching to another tool will clear your chat history and remove incompatible files. Are you sure you want to continue?';
      } else if (uploadedFiles.length > 0 && selectedTool !== apiLabel) {
        // Even if files are compatible, warn when switching tools with files uploaded
        shouldWarn = true;
        warningMessage = 'Switching tools will clear your chat history and all uploaded files. Are you sure you want to continue?';
      }

      // If we should warn, show confirmation dialog
      if (shouldWarn) {
        setConfirmationConfig({
          title: 'Switch Tool?',
          message: warningMessage,
          onConfirm: () => {
            // Clear history and files
            if (onClearChat) {
              onClearChat();
            }
            dispatch(clearUploadedFiles());
            // Also clear the ref immediately so proceedWithToolSelection doesn't read stale
            // file data before the Redux dispatch triggers a re-render.
            uploadedFilesRef.current = [];
            setShowConfirmation(false);
            setConfirmationConfig(null);
            // Now set the new tool
            proceedWithToolSelection(apiLabel);
            dispatch(clearTranslationData());
            setIsTranslationCollapsed(false);
          }
        });
        setShowConfirmation(true);
        // Don't proceed - keep existing tool if user cancels
        return;
      }
    }

    // Check if switching from a model-eligible context (standard chat, reasoning, web_search)
    // to a non-model-eligible tool (image_generation, data_analysis, translation).
    // These tools don't support model selection, so previous chat history must be cleared
    // to avoid stale model/queryType leaking into the request payload.
    const isCurrentModelEligible = !selectedTool || MODEL_ELIGIBLE_TOOLS.has(selectedTool);
    const isTargetModelEligible = MODEL_ELIGIBLE_TOOLS.has(apiLabel);
    if (isCurrentModelEligible && !isTargetModelEligible && hasMessages && uploadedFiles.length === 0) {
      setConfirmationConfig({
        title: 'Clear Chat History?',
        message: 'This tool requires a fresh conversation. Your current chat history will be cleared. Are you sure you want to continue?',
        onConfirm: () => {
          if (onClearChat) {
            onClearChat();
          }
          setShowConfirmation(false);
          setConfirmationConfig(null);
          proceedWithToolSelection(apiLabel);
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
        }
      });
      setShowConfirmation(true);
      return;
    }

    // Check if switching from one tool to another (when there are messages but no files)
    if (selectedTool && selectedTool !== apiLabel && hasMessages && uploadedFiles.length === 0 && shouldClearForTargetTool(apiLabel)) {
      // Show confirmation dialog for tool switching only if there are messages
      setConfirmationConfig({
        title: 'Switch Tool?',
        message: 'Switching tools will clear your chat history. Are you sure you want to continue?',
        onConfirm: () => {
          // Clear history
          if (onClearChat) {
            onClearChat();
          }
          setShowConfirmation(false);
          setConfirmationConfig(null);
          // Now set the new tool
          proceedWithToolSelection(apiLabel);
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
        }
      });
      setShowConfirmation(true);
      return;
    }

    // No confirmation needed, proceed directly
    proceedWithToolSelection(apiLabel);
  };

  // ─── handleRemoveTool ────────────────────────────────────────────────────────

  const handleRemoveTool = () => {
    const shouldClearChat = shouldClearForTargetTool(null);

    // Show confirmation dialog for tool removal only if there are messages and the destination actually changes conversation context
    if (hasMessages && shouldClearChat) {
      setConfirmationConfig({
        title: 'Remove Tool?',
        message: 'Removing this tool will clear your chat history and all uploaded files. Are you sure you want to continue?',
        onConfirm: () => {
          // Clear history and reset to home state when tool is removed
          if (onClearChat) {
            onClearChat();
          }

          manuallyClearedToolRef.current = true;
          dispatch(setSelectedTool(null));
          setAwaitingFileSelection(false);
          dispatch(clearUploadedFiles());
          dispatch(clearTranslationData());
          setIsTranslationCollapsed(false);
          dispatch(hideGlobalBlocking());
          setShowConfirmation(false);
          setConfirmationConfig(null);
        }
      });
      setShowConfirmation(true);
    } else {
      // No confirmation needed if there are no messages or the conversation already belongs to regular chat.
      if (shouldClearChat && onClearChat) {
        onClearChat();
      }
      manuallyClearedToolRef.current = true;
      dispatch(setSelectedTool(null));
      setAwaitingFileSelection(false);
      dispatch(clearUploadedFiles());
      dispatch(clearTranslationData());
      setIsTranslationCollapsed(false);
      dispatch(hideGlobalBlocking());
    }
  };

  // ─── Gallery tool selection listener ─────────────────────────────────────────

  React.useEffect(() => {
    const handleGalleryToolSelection = (event: Event) => {
      if (isSpeechListening) return;
      const customEvent = event as CustomEvent<{ tool: string }>;
      const toolName = customEvent.detail.tool;

      logger.log('[ChatInput] Gallery tool selection event received:', toolName);

      const toolMapping: { [key: string]: string } = {
        'image_generation': 'image_generation',
        'web_search': 'web_search',
        'reasoning': 'reasoning',
        'data_analysis': 'data_analysis',
        'upload_file': 'upload_file',
        'none': 'none',
        'translation': 'translation'
      };

      const apiLabel = toolMapping[toolName];

      logger.log('[ChatInput] Mapped to apiLabel:', apiLabel);

      if (apiLabel === 'upload_file') {
        logger.log('[ChatInput] Triggering file upload dialog');
        triggerAdditionalUpload();
      } else if (apiLabel === 'none') {
        logger.log('[ChatInput] Clearing tool selection');
        manuallyClearedToolRef.current = true;
        dispatch(setSelectedTool(null));
        dispatch(clearTargetLanguages());
      } else if (apiLabel && apiLabel !== '') {
        logger.log('[ChatInput] Proceeding with tool selection:', apiLabel);
        proceedWithToolSelection(apiLabel);
      }
    };

    window.addEventListener('galleryToolSelection', handleGalleryToolSelection);
    return () => window.removeEventListener('galleryToolSelection', handleGalleryToolSelection);
  }, [dispatch, proceedWithToolSelection, triggerAdditionalUpload, isSpeechListening]);

  // ─── Return ──────────────────────────────────────────────────────────────────

  return {
    showConfirmation,
    setShowConfirmation,
    confirmationConfig,
    setConfirmationConfig,
    handleOptionClick,
    handleRemoveTool,
    proceedWithToolSelection,
    handleModelSelection,
    triggerAdditionalUpload,
    manuallyClearedToolRef,
  };
}
